import { useCallback, useRef } from 'react';

import { useUnmount } from './use-unmount';

/**
 * A hook that manages timeouts for a component.
 *
 * It returns a function that can be used to add callbacks (`addDelayedCallback`)
 * to be triggered with a delay, and a function to clear all timeouts (`clearAll`).
 */
const useDelayedCallbackManager = () => {
    const timeoutRefs = useRef<Map<NodeJS.Timeout, () => void>>(new Map());

    const addDelayedCallback = useCallback((callback: () => void, delay: number) => {
        const timeoutId = setTimeout(() => {
            callback();
            /** remove the timeout from the map after it executes */
            timeoutRefs.current.delete(timeoutId);
        }, delay);
        timeoutRefs.current.set(timeoutId, callback);
        return timeoutId;
    }, []);

    const clearAll = useCallback(() => {
        timeoutRefs.current.forEach((_, timeoutId) => {
            clearTimeout(timeoutId);
        });
        timeoutRefs.current.clear();
    }, []);

    useUnmount(clearAll);

    return { addDelayedCallback, clearAll };
};

export { useDelayedCallbackManager };
